import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin, User, Tag, Calendar, MessageSquare, Trash2, Star, Loader2, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { ConexionAPI, TIPO_META, ROL_META, haceCuanto, formatearPrecio } from "../../lib/conexion";
import { checkoutDestacar, pollEstadoPago, fetchBeneficios } from "../../lib/pagos";
import { useAuth } from "../../context/AuthContext";
import { PlanBadge } from "../../components/PlanBadge";

const DESTACAR_OPCIONES = [
  { dias: 3, precio: 9.90, etiqueta: "Prueba" },
  { dias: 7, precio: 14.90, etiqueta: "Semanal", popular: true },
  { dias: 15, precio: 24.90, etiqueta: "Quincenal" },
  { dias: 30, precio: 39.90, etiqueta: "Mensual" },
];

export default function ConexionDetallePublicacion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [showDestacar, setShowDestacar] = useState(false);
  const [destLoading, setDestLoading] = useState(null);
  const [beneficios, setBeneficios] = useState(null);

  function cargar() {
    return ConexionAPI.detallePublicacion(id).then(setPub).catch((e) => setError(e?.response?.data?.detail || "No encontrada")).finally(() => setLoading(false));
  }

  function abrirDestacar() {
    fetchBeneficios().then(setBeneficios).catch(() => {});
    setShowDestacar(true);
  }

  useEffect(() => {
    cargar();
    const sid = params.get("session_id");
    if (sid) {
      pollEstadoPago(sid, () => {}).then((d) => {
        if (d.payment_status === "paid") {
          toast.success("¡Publicación destacada! Ahora aparece primero en las búsquedas.");
          cargar();
        } else if (d.payment_status === "timeout") {
          toast.info("Confirmando tu pago… se reflejará en breve.");
        }
      }).finally(() => setParams({}, { replace: true }));
    }
    // eslint-disable-next-line
  }, [id]);

  async function destacar(dias) {
    setDestLoading(dias);
    try {
      const res = await checkoutDestacar(id, dias);
      if (res.gratis) {
        setShowDestacar(false);
        setDestLoading(null);
        toast.success(`¡Publicación destacada GRATIS! Beneficio Elite. Te quedan ${res.restantes} este mes.`);
        cargar();
        return;
      }
      window.location.href = res.url;
    } catch (e) {
      toast.error("No se pudo iniciar el pago.");
      setDestLoading(null);
    }
  }

  if (loading) return <div className="text-center py-16 text-slate-500 text-sm">Cargando…</div>;
  if (error || !pub) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-slate-600 font-semibold">{error || "Publicación no encontrada."}</p>
        <Link to="/conexion" className="mt-3 inline-block text-brand-red font-semibold hover:underline">Volver al Ecosistema</Link>
      </div>
    );
  }

  const meta = TIPO_META[pub.tipo];
  const rolMeta = ROL_META[pub.usuario_rol] || {};
  const esPropia = user?.id === pub.usuario_id;

  async function contactar() {
    if (esPropia) return;
    setStarting(true);
    try {
      const { conversacion_id } = await ConexionAPI.abrirConversacion(pub.usuario_id);
      navigate(`/conexion/mensajes?conv=${conversacion_id}`);
    } finally {
      setStarting(false);
    }
  }

  async function eliminar() {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    await ConexionAPI.eliminarPublicacion(pub.id);
    navigate("/conexion/mis-publicaciones");
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid={`conexion-detalle-${pub.id}`}>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft size={16} /> Volver
      </button>

      <article className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        <div className="p-6 md:p-8" style={{ background: `linear-gradient(135deg, #FFFFFF 60%, ${meta.tint} 100%)` }}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"
              style={{ color: meta.color, backgroundColor: meta.tint }}
            >
              {meta.label}
            </span>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
              <Calendar size={12} /> {haceCuanto(pub.fecha)}
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">{pub.titulo}</h1>

          {pub.precio != null && (
            <p className="mt-3 text-3xl font-extrabold" style={{ color: meta.color }}>
              {formatearPrecio(pub.precio)}
            </p>
          )}
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Descripción</p>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{pub.descripcion}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-slate-100 rounded-xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Ubicación</p>
              <p className="text-sm text-slate-700 inline-flex items-center gap-2 font-medium">
                <MapPin size={14} className="text-brand-blue" />
                {pub.ubicacion.distrito}, {pub.ubicacion.provincia}, {pub.ubicacion.region}
              </p>
            </div>
            <div className="border border-slate-100 rounded-xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Categoría</p>
              <p className="text-sm text-slate-700 inline-flex items-center gap-2 font-medium">
                <Tag size={14} className="text-brand-blue" />
                {pub.categoria}
              </p>
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Publicado por</p>
            <Link
              to={`/conexion/perfil/${pub.usuario_id}`}
              className="flex items-center gap-3 group"
              data-testid="conexion-detalle-autor"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                {pub.usuario_nombre?.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate group-hover:text-brand-blue transition-colors inline-flex items-center gap-1.5">{pub.usuario_nombre}<PlanBadge plan={pub.usuario_plan} /></p>
                {rolMeta.label && (
                  <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: rolMeta.color }}>
                    {rolMeta.label}
                  </p>
                )}
              </div>
              <span className="ml-auto text-xs font-semibold text-brand-blue group-hover:text-brand-red whitespace-nowrap">
                Ver perfil y vitrina →
              </span>
            </Link>
          </div>

          <div className="flex flex-wrap gap-3">
            {!esPropia ? (
              <button
                onClick={contactar}
                disabled={starting}
                data-testid="conexion-detalle-contactar"
                className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-sm"
              >
                <MessageSquare size={16} /> {starting ? "Abriendo chat…" : "Contactar"}
              </button>
            ) : (
              <>
                {pub.destacado ? (
                  <span className="inline-flex items-center gap-2 bg-brand-red text-white font-semibold px-5 py-2.5 rounded-xl" data-testid="conexion-detalle-destacada">
                    <Star size={16} /> Publicación destacada
                  </span>
                ) : (
                  <button
                    onClick={abrirDestacar}
                    data-testid="conexion-detalle-destacar"
                    className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-sm"
                  >
                    <Star size={16} /> Promocionar publicación
                  </button>
                )}
                <button
                  onClick={eliminar}
                  data-testid="conexion-detalle-eliminar"
                  className="inline-flex items-center gap-2 border border-red-200 text-brand-red hover:bg-red-50 font-semibold px-5 py-2.5 rounded-xl transition-colors"
                >
                  <Trash2 size={16} /> Eliminar
                </button>
              </>
            )}
          </div>
        </div>
      </article>

      {showDestacar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" role="dialog" aria-modal="true" data-testid="destacar-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">Promocionar publicación</h3>
              <button onClick={() => setShowDestacar(false)} className="p-1 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Cerrar"><X size={18} /></button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 bg-orange-50 rounded-xl px-4 py-3 mb-5">
                <Star size={18} className="text-orange-500 fill-orange-500" />
                <span className="text-sm text-slate-700">Promocionar: <span className="font-bold text-slate-900">{pub.titulo}</span></span>
              </div>

              <h4 className="text-xl font-extrabold text-slate-900">Elige la duración</h4>
              <p className="text-sm text-slate-500 mt-0.5 mb-4">Tu publicación aparecerá destacada en los resultados</p>

              {beneficios?.destacados_gratis_restantes > 0 && (
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="destacar-beneficio-elite">
                  <PlanBadge plan="elite" />
                  <span className="text-sm text-amber-800 font-medium">Tienes {beneficios.destacados_gratis_restantes} promoción(es) GRATIS este mes.</span>
                </div>
              )}

              <div className="space-y-3">
                {DESTACAR_OPCIONES.map((o) => {
                  const gratis = beneficios?.destacados_gratis_restantes > 0;
                  const porDia = (o.precio / o.dias).toFixed(2);
                  return (
                    <button
                      key={o.dias}
                      onClick={() => destacar(o.dias)}
                      disabled={destLoading != null}
                      data-testid={`destacar-opcion-${o.dias}`}
                      className={`relative w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl border-2 text-left transition-all disabled:opacity-60 ${o.popular ? "border-orange-400 bg-orange-50/40" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      {o.popular && (
                        <span className="absolute -top-2.5 right-4 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full">Popular</span>
                      )}
                      <span className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${o.popular ? "bg-orange-100" : "bg-orange-50"}`}>
                        <Zap size={20} className="text-orange-500 fill-orange-500" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-lg font-extrabold text-slate-900 leading-tight">{o.dias} días</span>
                        <span className="block text-xs text-slate-500 font-medium">{o.etiqueta}</span>
                      </span>
                      <span className="text-right flex-shrink-0">
                        {gratis ? (
                          <span className="block text-lg font-extrabold text-emerald-600">GRATIS</span>
                        ) : (
                          <span className="block text-lg font-extrabold text-orange-500 inline-flex items-center gap-1.5">
                            {destLoading === o.dias && <Loader2 size={14} className="animate-spin" />}
                            S/. {o.precio.toFixed(2)}
                          </span>
                        )}
                        <span className="block text-[11px] text-slate-400 font-medium">S/. {porDia}/día</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-center text-[11px] text-slate-400 pt-4">Pago seguro con Mercado Pago · Modo de prueba</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
